// Shots: the museum sessions' framings. Each is a real moment at true scale,
// the camera placed from the Sun's direction round one body, so the light,
// the shadows and the moons are what they were then: main.js (startShot)
// sets the scale and the clock, turns the bodies listed in `iau` onto their
// IAU poles and meridians (src/photos/geometry.js), and holds the camera
// here every frame while the shot runs. In VR the head rides that camera,
// so `look` is where a guest faces when the shot opens.
//
//   body    the body framed (a viewer id: 'saturn', 'jupiter', 'io', ...)
//   at      the moment, UTC
//   speed   the clock's pace: sim seconds per real second, or 'real'
//   frame   the plane `phase` and `elev` are measured in: 'equator' (the
//           body's IAU equator, which for Saturn is the ring plane) or
//           'ecliptic'
//   phase   degrees round the frame's pole from the Sun's direction (0: the
//           Sun at your back; 180: behind the body, in its shadow), counted
//           eastward, the way the body turns
//   elev    degrees above the frame's plane (north +)
//   dist    distance from the body's centre in its radii
//   look    { yaw, pitch } degrees from straight at the body's centre (yaw +
//           to the left, pitch + up), or omitted
//   iau     bodies to set on their IAU poles (default: the framed body)
//   ringshine  Saturn's night side lit by its rings (gasGiant.js)
import * as THREE from 'three';

export const SHOTS = {
  // Cassini's last full day (its last signal reached the Earth at 11:55 UT
  // on 15 September 2017, NASA): northern summer, the Sun high over the
  // rings' north face, the globe's shadow across them, high over the lit side.
  'saturn-finale': { body: 'saturn', at: '2017-09-14T12:00:00Z', speed: 0, frame: 'equator', phase: 50, elev: 32, dist: 3.4, look: { yaw: 0, pitch: -8 } },
  // In Saturn's shadow, the Sun hidden behind the globe: on the line away
  // from the Sun, which that day stood 26.7 degrees over the rings' north
  // face, so the rings are seen from their unlit south face.
  'saturn-shadow': { body: 'saturn', at: '2017-09-14T12:00:00Z', speed: 0, frame: 'equator', phase: 180, elev: -24, dist: 4.5, ringshine: 0.22 },
  // Just under the rings, the B ring arching overhead.
  'saturn-under': { body: 'saturn', at: '2017-09-14T12:00:00Z', speed: 0, frame: 'equator', phase: 25, elev: -2.5, dist: 1.7, look: { yaw: 0, pitch: 18 } },
  // Io's shadow on Jupiter at the middle of a transit (4 November 2026,
  // 16:47 to 19:05 UT in the viewer's models), from just west of the Sun's
  // line so Io stands off its shadow; the clock at 60x so the shadow and the
  // clouds move.
  'jupiter-io-shadow': { body: 'jupiter', at: '2026-11-04T17:55:00Z', speed: 60, frame: 'equator', phase: -12, elev: 8, dist: 2.4 },
  // Io itself, 16,000 km off, lit from beside you, with Jupiter and the
  // shadow beyond it.
  'io-jupiter': { body: 'io', at: '2026-11-04T17:55:00Z', speed: 60, frame: 'ecliptic', phase: 40, elev: 6, dist: 9, look: { yaw: -20, pitch: 0 } },
};

const _s = new THREE.Vector3();
const _e = new THREE.Vector3();
const _d = new THREE.Vector3();
const _q = new THREE.Quaternion();

// Where the camera stands and looks, from the body's centre and radius, the
// unit direction to the Sun and the frame's unit pole (scene axes):
// { position, target, up }.
export function shotPose(shot, centre, radius, toSun, pole, out = { position: new THREE.Vector3(), target: new THREE.Vector3(), up: new THREE.Vector3() }) {
  const deg = THREE.MathUtils.degToRad;
  // The Sun's direction in the frame's plane, and east of it.
  _s.copy(toSun).addScaledVector(pole, -toSun.dot(pole));
  if (_s.lengthSq() < 1e-12) _s.set(1, 0, 0).addScaledVector(pole, -pole.x);
  _s.normalize();
  _e.crossVectors(pole, _s);
  const ph = deg(shot.phase || 0);
  const el = deg(shot.elev || 0);
  _d.copy(_s).multiplyScalar(Math.cos(ph)).addScaledVector(_e, Math.sin(ph)).multiplyScalar(Math.cos(el)).addScaledVector(pole, Math.sin(el)).normalize();
  const dist = (shot.dist || 3) * radius;
  out.position.copy(centre).addScaledVector(_d, dist);
  // Looking at the centre, then turned by `look` about the frame's pole and
  // the camera's right.
  const fwd = _d.negate();
  out.up.copy(pole);
  if (shot.look) {
    if (shot.look.yaw) fwd.applyQuaternion(_q.setFromAxisAngle(pole, deg(shot.look.yaw)));
    if (shot.look.pitch) {
      const right = _e.crossVectors(fwd, pole).normalize();
      fwd.applyQuaternion(_q.setFromAxisAngle(right, deg(shot.look.pitch)));
    }
  }
  out.target.copy(out.position).addScaledVector(fwd, dist);
  return out;
}

// Travel between shots: one continuous flight, quick through the empty
// middle and slow at both ends. Progress is counted in e-folds of distance
// (sigma), so equal steps look equal, and runs on a smoothstep in time.
// Three parts: straight back along shot A's view until its body is a small
// disc (or a third of the way to the next, or 2.5 times the shot's
// distance), across on a curve that keeps clear of both bodies, and straight
// in along shot B's view. The legs at each end are pure moves back and
// forward, with the stars still; the camera turns only round the crossing,
// and in the headset all at once under a blink.
export const FLIGHT = {
  rate: 0.75, // e-folds of distance a second, on average
  minSeconds: 6,
  dotRadii: 573, // a body this many radii away is 0.2 degrees across
  partWay: 0.3,
  outAtLeast: 2.5,
  turnMargin: 1.5, // e-folds either side of the crossing the 2D turn takes
  cutAcross: 0.8, // e-folds the crossing takes under the headset's blink
  blinkIn: 0.35, // seconds to black before the crossing
  blinkOut: 0.5, // and back after it
};

const smooth = (x) => x * x * (3 - 2 * x);
const smoothstep = (a, b, x) => smooth(Math.min(1, Math.max(0, (x - a) / (b - a))));
// The time fraction at which smoothstep reaches y.
const unsmooth = (y) => 0.5 - Math.sin(Math.asin(1 - 2 * Math.min(1, Math.max(0, y))) / 3);

const _p0 = new THREE.Vector3();
const _p1 = new THREE.Vector3();
const _seg = new THREE.Vector3();
const _lift = new THREE.Vector3();
const _v = new THREE.Vector3();

// The two ends of a crossing: where leg A ends and leg B starts.
function crossEnds(plan, a, b) {
  _p0.copy(a.position).addScaledVector(a.forward, -(plan.rOut - a.dist));
  _p1.copy(b.position).addScaledVector(b.forward, -(plan.rIn - b.dist));
}

// The crossing in e-folds too: x along it from where leg A ends, the range
// taken as rOut + x from A's end and rIn + (len - x) from B's, so it goes
// on growing out of A and shrinking into B as the legs did. `g` is the whole
// crossing's e-folds; with `tau`, the distance along at that part of them.
function crossing(rOut, rIn, len, tau) {
  const xm = Math.min(len, Math.max(0, (len + rIn - rOut) / 2));
  const g1 = Math.log((rOut + xm) / rOut);
  const g = g1 + Math.log((rIn + len - xm) / rIn);
  if (tau === undefined) return g;
  const y = tau * g;
  return y <= g1 ? rOut * Math.expm1(y) : rIn + len - (rIn + len - xm) * Math.exp(g1 - y);
}

/**
 * The flight from pose a to pose b, each { position, forward (unit), dist
 * (to what it looks at), radius (its body's), centre (its body's) }.
 * `cut`: the headset's version, the turn at one instant under a blink.
 */
export function planFlight(a, b, { cut = false } = {}) {
  const F = FLIGHT;
  const gap = _v.copy(a.position).addScaledVector(a.forward, a.dist).distanceTo(_p0.copy(b.position).addScaledVector(b.forward, b.dist));
  const reach = (d, r) => Math.max(d, Math.min(Math.max(F.partWay * gap, F.outAtLeast * d), F.dotRadii * r));
  const plan = { rOut: reach(a.dist, a.radius), rIn: reach(b.dist, b.radius), cut };
  plan.l1 = Math.log(plan.rOut / a.dist);
  plan.l3 = Math.log(plan.rIn / b.dist);
  crossEnds(plan, a, b);
  // Across a little quicker than the legs (at most 6 e-folds, where Saturn
  // to Jupiter is about 8.4), quickest where nothing is near.
  plan.l2 = cut ? F.cutAcross : Math.min(6, Math.max(1, crossing(plan.rOut, plan.rIn, _p0.distanceTo(_p1))));
  plan.S = plan.l1 + plan.l2 + plan.l3;
  plan.T = Math.max(F.minSeconds, plan.S / F.rate);
  plan.tAcross = plan.T * unsmooth(plan.l1 / plan.S);
  plan.tIn = plan.T * unsmooth((plan.l1 + plan.l2) / plan.S);
  return plan;
}

// Where the flight is at t seconds: { sigma, part ('out' | 'across' | 'in'),
// across (0..1 through the crossing), turn (0: shot A's orientation, 1: B's),
// blink (0..1 black, the headset's), done }.
export function flightProgress(plan, t) {
  const sigma = plan.S * smooth(Math.min(1, Math.max(0, t / plan.T)));
  const across = Math.min(1, Math.max(0, (sigma - plan.l1) / plan.l2));
  const part = sigma < plan.l1 ? 'out' : sigma < plan.l1 + plan.l2 ? 'across' : 'in';
  let turn, blink = 0;
  if (plan.cut) {
    turn = across >= 0.5 ? 1 : 0;
    blink = smoothstep(plan.tAcross - FLIGHT.blinkIn, plan.tAcross, t) * (1 - smoothstep(plan.tIn, plan.tIn + FLIGHT.blinkOut, t));
  } else {
    turn = smoothstep(plan.l1 - FLIGHT.turnMargin, plan.l1 + plan.l2 + FLIGHT.turnMargin, sigma);
  }
  return { sigma, part, across, turn, blink, done: t >= plan.T * (1 - 1e-9) };
}

// The camera's position at progress sigma, between live poses a and b.
export function flightPoint(plan, sigma, a, b, out = new THREE.Vector3()) {
  if (sigma <= plan.l1) return out.copy(a.position).addScaledVector(a.forward, -(a.dist * Math.exp(sigma) - a.dist));
  if (sigma >= plan.l1 + plan.l2) return out.copy(b.position).addScaledVector(b.forward, -(b.dist * Math.exp(plan.S - sigma) - b.dist));
  crossEnds(plan, a, b);
  _seg.subVectors(_p1, _p0);
  const len2 = Math.max(_seg.lengthSq(), 1e-30);
  const len = Math.sqrt(len2);
  const tau = crossing(plan.rOut, plan.rIn, len, (sigma - plan.l1) / plan.l2) / len;
  // A quadratic curve, its middle lifted off any body the straight line
  // passes within 0.7 of its leg's reach: the lift at the closest point is
  // 2 s (1 - s) times the control point's.
  _lift.set(0, 0, 0);
  for (const [c, clear] of [[a.centre, 0.7 * plan.rOut], [b.centre, 0.7 * plan.rIn]]) {
    const s = Math.min(1, Math.max(0, _v.subVectors(c, _p0).dot(_seg) / len2));
    _v.copy(_p0).addScaledVector(_seg, s).sub(c);
    const d = _v.length();
    if (d >= clear) continue;
    const k = 2 * Math.max(0.02, s) * (1 - Math.min(0.98, s));
    // Straight through the centre: off to any side.
    if (d < 1e-9 * len) {
      _v.set(0, 1, 0).cross(_seg);
      if (_v.lengthSq() < 1e-12 * len2) _v.set(1, 0, 0).cross(_seg);
    }
    _lift.addScaledVector(_v.normalize(), Math.min(len, (clear - d) / k));
  }
  return out.copy(_p0).lerp(_p1, tau).addScaledVector(_lift, 2 * tau * (1 - tau));
}
